import { Format, Routing } from "@common/_constants";
import { Button } from "@common/components/Atoms/Button";
import Card, { CardItem } from "@common/components/Atoms/Card";
import Error from "@common/components/Atoms/Error";
import Loading from "@common/components/Atoms/Loading";
import LocalTime from "@common/components/Atoms/LocalTime";
import { SearchEntry } from "@common/components/Atoms/SearchFilters";
import Sort from "@common/components/Atoms/Sort";
import Dropdown, {
    DropdownItem,
    DropdownMenu,
} from "@common/components/Molecules/Dropdown";
import Facets from "@common/components/Molecules/Facets";
import FilteredList, {
    FilteredListCardsView,
    FilteredListTableView,
} from "@common/components/Molecules/FilteredList";
import Modal from "@common/components/Molecules/Modal";
import SelectedFacets from "@common/components/Molecules/SelectedFacets";
import { Facet as FacetModel } from "@common/models";
import { GlobalConfig } from "@common/utils";
import {
    CandidateSearchOptions,
    ReferralService,
    SearchOptions,
    SearchService,
} from "@pro/_services";
import {
    ReferralsPageHeaderCenter,
    ReferralsSortDropdown,
} from "@pro/components/Organisms/Referrals";
import { EditReferral } from "@pro/containers/Modals/EditReferral";
import { ReferCandidate } from "@pro/containers/Modals/Refer";
import { SendMessage } from "@pro/containers/Modals/SendMessage";
import { PageInternal } from "@pro/containers/PageInternal";
import moment from "moment";
import * as React from "react";
import { Link, RouteComponentProps } from "react-router-dom";

interface ReferralsState {
    error: boolean;
    errorMessage: string;
    updating: boolean;
    loading: boolean;
    isLargeScreen: boolean;
    triggerOnUpdateUrl: boolean;
    jobCandidateForModals?: SearchResult;
    inviteFriendsModalIsOpen: boolean;
    sendMessageModalIsOpen: boolean;
    filtersModalIsOpen: boolean;
    referCandidateModalIsOpen: boolean;
    editReferralModalIsOpen: boolean;
    applyForJobModalIsOpen: boolean;
    shareJobModalIsOpen: boolean;
    activeJobId: null;
    search: CandidateSearchOptions;
    searchResults?: {
        results: SearchResult[];
        selected_facets: FacetModel[];
        facets: FacetModel[];
    };
}

interface SearchResult {
    total_commission_in_us_dollars: string;
    hired_timestamp: string;
    interviewing_timestamp: string;
    job_candidate_id: number;
    job_id: number;
    job_name: string;
    phase_name: string;
    user_id: number;
    user_full_name: string;
    user_manager_user_id: number;
}

interface ReferralsProps extends RouteComponentProps<any> {}

export class Referrals extends React.Component<ReferralsProps, ReferralsState> {
    private _abortControllers: { [key: string]: AbortController } = {};
    defaultOrderBy: number;

    constructor(props) {
        super(props);

        this.defaultOrderBy = 1;

        this.onPageChange = this.onPageChange.bind(this);
        this.onFacetChange = this.onFacetChange.bind(this);
        this.onSortChange = this.onSortChange.bind(this);
        this.onKeywordSubmit = this.onKeywordSubmit.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.openModal = this.openModal.bind(this);
        this.onUrlChange = this.onUrlChange.bind(this);
        this.onReferCandidateOkay = this.onReferCandidateOkay.bind(this);
        this.onEditReferralOkay = this.onEditReferralOkay.bind(this);

        this.emptySearchOptions = this.emptySearchOptions.bind(this);
        this.loadPageForJob = this.loadPageForJob.bind(this);
        this.goToReferral = this.goToReferral.bind(this);
        this.reloadPage = this.reloadPage.bind(this);
        this.fetchReferrals = this.fetchReferrals.bind(this);

        this.searchService = new SearchService(
            this.emptySearchOptions,
            this.fetchReferrals,
            this.props.history
        );

        this.state = {
            error: false,
            errorMessage: "",
            updating: false,
            loading: true,
            isLargeScreen: false,
            triggerOnUpdateUrl: true,
            jobCandidateForModals: undefined,
            inviteFriendsModalIsOpen: false,
            sendMessageModalIsOpen: false,
            filtersModalIsOpen: false,
            referCandidateModalIsOpen: false,
            editReferralModalIsOpen: false,
            applyForJobModalIsOpen: false,
            shareJobModalIsOpen: false,
            activeJobId: null,
            search: this.emptySearchOptions(),
            searchResults: undefined,
        };
    }

    searchService: SearchService;
    removeHistoryListener: () => void;

    componentDidMount() {
        this.setState({
            isLargeScreen: window.matchMedia(
                "(min-width:" + GlobalConfig.ui.breakpoints.md + "px)"
            ).matches,
        });
        window.onresize = () => {
            this.setState({
                isLargeScreen: window.matchMedia(
                    "(min-width:" + GlobalConfig.ui.breakpoints.md + "px)"
                ).matches,
            });
        };

        this.searchService.updateFromUrl(this.props.location);
        this.removeHistoryListener = this.props.history.listen(
            this.onUrlChange
        );
    }

    componentWillUnmount() {
        if (this.removeHistoryListener) this.removeHistoryListener();
        const abortControllers = this._abortControllers;
        if (Object.keys(abortControllers).length > 0) {
            Object.keys(abortControllers).map(key =>
                abortControllers[key].abort()
            );
        }
    }

    emptySearchOptions(): CandidateSearchOptions {
        return {
            search_term: "",
            current_page: 1,
            page_size: 25,
            order_by: this.defaultOrderBy,
            facets: [] as FacetModel[],
            job_id: undefined,
            my_applications: false,
        };
    }

    onUrlChange(location) {
        this.searchService.updateFromUrl(location);
    }

    reloadPage() {
        this.searchService.updateFromUrl(this.props.location);
    }

    goToReferral(id: number) {
        this.props.history.push("/referrals/" + id);
    }

    loadPageForJob(jobId: number) {
        this.searchService.updateUrl({
            ...(this.state.search as SearchOptions),
            job_id: jobId,
        });
    }

    fetchReferrals(options: CandidateSearchOptions) {
        this.setState({ updating: true }, () => {
            // Check to see if there already was an abortController, and if so, call abort on it
            if (this._abortControllers.search) {
                this._abortControllers.search.abort();
            }
            this._abortControllers.search = new AbortController();
            const searchSignal = this._abortControllers.search.signal;

            ReferralService.search(options, searchSignal)
                .then(result => {
                    const defaultSearch = this.emptySearchOptions();
                    const search = {
                        search_term:
                            result.search_term || defaultSearch.search_term,
                        current_page:
                            result.current_page || defaultSearch.current_page,
                        page_size: result.page_size || defaultSearch.page_size,
                        order_by: result.order_by || defaultSearch.order_by,
                        facets: result.selected_facets || defaultSearch.facets,
                        job_id: result.job_id || defaultSearch.job_id,
                        my_applications: defaultSearch.my_applications,
                    };

                    this.setState({
                        error: false,
                        updating: false,
                        loading: false,
                        search,
                        searchResults: result,
                    });
                })
                .catch(err => {
                    console.log(err);
                    if (err.type === "abort" || err.name === "AbortError") {
                        return;
                    }

                    this.setState({
                        error: true,
                        errorMessage: err.message,
                        updating: false,
                        loading: false,
                    });
                });
        });
    }

    closeModal(modal: string) {
        const newState = {};
        newState[modal] = false;
        this.setState(newState);
    }

    openModal(
        modal: string,
        jobCandidateForModals: SearchResult = this.state.jobCandidateForModals!
    ) {
        const newState = {} as any;
        newState[modal] = true;
        newState.jobCandidateForModals = jobCandidateForModals;
        this.setState(newState);
    }

    onPageChange(pageNumber: number) {
        this.searchService.updateUrl({
            ...(this.state.search as SearchOptions),
            current_page: pageNumber,
        });
    }

    onFacetChange(facet: FacetModel) {
        this.closeModal("filtersModalIsOpen");

        const options = { ...(this.state.search as SearchOptions) };
        const newFacets = !!options.facets
            ? [...options.facets.filter(f => f.type !== facet.type), facet]
            : [facet];
        options.facets = newFacets;
        options.current_page = 1;
        this.searchService.updateUrl(options);
    }

    onSortChange(sort: number) {
        this.searchService.updateUrl({
            ...(this.state.search as SearchOptions),
            order_by: sort,
            current_page: 1,
        });
    }

    onKeywordSubmit(keyword: string) {
        if (keyword !== this.state.search.search_term) {
            this.searchService.updateUrl({
                ...(this.state.search as SearchOptions),
                search_term: keyword,
                order_by: 3,
            });
        }
        this.closeModal("filtersModalIsOpen");
    }

    onFiltersButtonClick() {
        this.openModal("filtersModalIsOpen");
    }

    onReferCandidateOkay(id: number) {
        this.closeModal("referCandidateModalIsOpen");
        this.goToReferral(id);
    }

    onEditReferralOkay() {
        this.closeModal("editReferralModalIsOpen");
        this.reloadPage();
    }

    render() {
        const {
            error,
            updating,
            loading,
            isLargeScreen,
            editReferralModalIsOpen,
            sendMessageModalIsOpen,
            jobCandidateForModals,
        } = this.state;

        if (error) {
            return (
                <PageInternal title="referrals">
                    <Error
                        show={this.state.error}
                        description={this.state.errorMessage}
                    />
                </PageInternal>
            );
        }

        if (loading) {
            return (
                <PageInternal title="referrals">
                    <Loading />
                </PageInternal>
            );
        }

        if (
            !this.state.searchResults ||
            this.state.searchResults.results.length === 0
        ) {
            return (
                <PageInternal title="referrals">
                    <h2 className="c-heading c-heading--purple">
                        we couldn't find any matching referrals, but try
                        expanding your search or finding a job to refer a
                        candidate.
                    </h2>
                    <div className="u-text-center">
                        <div className="c-block c-block--space-evenly c-block--narrow">
                            <Link
                                className="c-button c-button--green"
                                to={Routing.Client.Pro.Referrals.SEARCH}
                            >
                                expand search
                            </Link>
                            <Button
                                id="refer-friend-button"
                                onClick={() =>
                                    this.openModal("referCandidateModalIsOpen")
                                }
                            >
                                refer a candidate
                            </Button>
                            <Link
                                className="c-button c-button"
                                to={Routing.Client.Pro.Jobs.SEARCH}
                            >
                                find jobs
                            </Link>
                        </div>
                    </div>

                    <ReferCandidate
                        isOpen={this.state.referCandidateModalIsOpen}
                        onOkay={this.onReferCandidateOkay}
                        onCancel={() =>
                            this.closeModal("referCandidateModalIsOpen")
                        }
                        closeModal={() =>
                            this.closeModal("referCandidateModalIsOpen")
                        }
                    />
                </PageInternal>
            );
        }

        return (
            <PageInternal
                updating={updating}
                title="referrals"
                center={
                    <ReferralsPageHeaderCenter
                        keyword={this.state.search.search_term || ""}
                        onKeywordSubmit={this.onKeywordSubmit}
                    />
                }
            >
                <FilteredList>
                    <FilteredListTableView
                        responsive={true}
                        data={this.state.searchResults}
                        onPageChange={this.onPageChange}
                        onFacetChange={this.onFacetChange}
                        isLargeScreen={isLargeScreen}
                    >
                        <table className="c-table c-table--referrals">
                            <thead>
                                <tr>
                                    <th>
                                        <Sort
                                            sortKey={1}
                                            orderBy={
                                                this.state.search.order_by ||
                                                this.defaultOrderBy
                                            }
                                            onSortChange={() =>
                                                this.onSortChange(1)
                                            }
                                        >
                                            Name
                                        </Sort>
                                    </th>
                                    <th>
                                        <Sort
                                            sortKey={4}
                                            orderBy={
                                                this.state.search.order_by ||
                                                this.defaultOrderBy
                                            }
                                            onSortChange={() =>
                                                this.onSortChange(4)
                                            }
                                        >
                                            Job
                                        </Sort>
                                    </th>
                                    <th>
                                        <Sort
                                            sortKey={2}
                                            orderBy={
                                                this.state.search.order_by ||
                                                this.defaultOrderBy
                                            }
                                            onSortChange={() =>
                                                this.onSortChange(2)
                                            }
                                        >
                                            Stage
                                        </Sort>
                                    </th>
                                    <th>
                                        <Sort
                                            sortKey={21}
                                            orderBy={
                                                this.state.search.order_by ||
                                                this.defaultOrderBy
                                            }
                                            onSortChange={() =>
                                                this.onSortChange(21)
                                            }
                                        >
                                            Referral Reward
                                        </Sort>
                                    </th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {this.state.searchResults.results.map(refr => (
                                    <tr key={refr.job_candidate_id}>
                                        <td>
                                            <Link
                                                to={
                                                    "/referrals/" +
                                                    refr.job_candidate_id
                                                }
                                                className="c-link no-underline c-table__full-name"
                                            >
                                                {refr.user_full_name}
                                            </Link>
                                        </td>
                                        <td>
                                            <Link
                                                to={"/jobs/" + refr.job_id}
                                                className="c-link no-underline c-table__job-name"
                                            >
                                                {refr.job_name}
                                            </Link>
                                        </td>
                                        <td>
                                            {refr.phase_name}
                                            <br />
                                            {refr.hired_timestamp && (
                                                <span className="c-table__datetime">
                                                    {moment
                                                        .utc()
                                                        .diff(
                                                            moment(
                                                                refr.hired_timestamp
                                                            )
                                                        ) > 0
                                                        ? "started "
                                                        : "starting "}
                                                    <LocalTime
                                                        utcTimestamp={
                                                            refr.hired_timestamp
                                                        }
                                                        format={
                                                            Format.Datetime
                                                                .DATE_ABBR_MONTH
                                                        }
                                                    />
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {refr.total_commission_in_us_dollars !==
                                            ""
                                                ? refr.total_commission_in_us_dollars
                                                : "N/A"}
                                        </td>
                                        <td>
                                            <Dropdown right={true}>
                                                <Button
                                                    id="referral-listitem-dropdown"
                                                    dropdown={true}
                                                    className="c-dropdown__toggle"
                                                    onClick={() =>
                                                        console.log(
                                                            "TODO: open dropdown"
                                                        )
                                                    }
                                                >
                                                    options
                                                </Button>
                                                <DropdownMenu>
                                                    <DropdownItem
                                                        onClick={() => {
                                                            this.openModal(
                                                                "sendMessageModalIsOpen",
                                                                refr
                                                            );
                                                        }}
                                                    >
                                                        Send Message
                                                    </DropdownItem>
                                                    <DropdownItem
                                                        onClick={() => {
                                                            this.openModal(
                                                                "editReferralModalIsOpen",
                                                                refr
                                                            );
                                                        }}
                                                    >
                                                        Edit Referral
                                                    </DropdownItem>
                                                    <DropdownItem
                                                        linkTo={`/referrals/${refr.job_candidate_id}/activity`}
                                                    >
                                                        View Activity
                                                    </DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </FilteredListTableView>

                    <FilteredListCardsView
                        responsive={true}
                        data={this.state.searchResults}
                        onPageChange={this.onPageChange}
                        onFiltersButtonClick={() => this.onFiltersButtonClick()}
                        sortDropdown={
                            <ReferralsSortDropdown
                                onSortChange={this.onSortChange}
                            />
                        }
                        isLargeScreen={isLargeScreen}
                    >
                        {this.state.searchResults.results.map(refr => (
                            <Card
                                ribbon={null}
                                id={refr.job_candidate_id.toString()}
                                linkTo={"/referrals/" + refr.job_candidate_id}
                                title={refr.user_full_name}
                                key={refr.job_candidate_id}
                            >
                                <CardItem bullet={true}>
                                    {refr.job_name}
                                </CardItem>
                                <CardItem bullet={true}>
                                    {refr.phase_name}
                                </CardItem>
                                {refr.hired_timestamp && (
                                    <CardItem bullet={true}>
                                        {moment
                                            .utc()
                                            .diff(
                                                moment(refr.hired_timestamp)
                                            ) > 0
                                            ? "Started "
                                            : "Starting "}
                                        <LocalTime
                                            utcTimestamp={refr.hired_timestamp}
                                            format={
                                                Format.Datetime.DATE_ABBR_MONTH
                                            }
                                        />
                                    </CardItem>
                                )}
                                <CardItem bullet={true}>
                                    {refr.total_commission_in_us_dollars}{" "}
                                    {refr.total_commission_in_us_dollars !== ""
                                        ? " referral reward"
                                        : "referral reward not available"}
                                </CardItem>
                            </Card>
                        ))}
                    </FilteredListCardsView>
                </FilteredList>

                <Modal
                    className="c-modal--filters"
                    size="full"
                    isOpen={this.state.filtersModalIsOpen}
                    closeModal={() => this.closeModal("filtersModalIsOpen")}
                >
                    <div className="c-modal__filters">
                        <div className="c-modal__filters-title">filters</div>

                        {this.state.searchResults.selected_facets &&
                            this.state.searchResults.selected_facets.length >
                                0 && (
                                <SelectedFacets
                                    overlay={true}
                                    data={
                                        this.state.searchResults.selected_facets
                                    }
                                    onFacetChange={this.onFacetChange}
                                />
                            )}

                        <SearchEntry
                            label="keywords"
                            overlay={true}
                            dark={true}
                            submitOnEnter={true}
                            keyword={this.state.search.search_term}
                            onSubmit={this.onKeywordSubmit}
                        />

                        <Facets
                            overlay={true}
                            facets={this.state.searchResults.facets}
                            onFacetChange={this.onFacetChange}
                        />
                    </div>
                </Modal>

                {jobCandidateForModals && (
                    <div>
                        <EditReferral
                            isOpen={editReferralModalIsOpen}
                            jobCandidateId={
                                jobCandidateForModals.job_candidate_id
                            }
                            onOkay={this.onEditReferralOkay}
                            onCancel={() =>
                                this.closeModal("editReferralModalIsOpen")
                            }
                            closeModal={() =>
                                this.closeModal("editReferralModalIsOpen")
                            }
                        />

                        <SendMessage
                            isOpen={sendMessageModalIsOpen}
                            contextJobId={jobCandidateForModals.job_id}
                            contextJobName={jobCandidateForModals.job_name}
                            contextUserId={jobCandidateForModals.user_id}
                            contextUserFullName={
                                jobCandidateForModals.user_full_name
                            }
                            toUserIds={[
                                jobCandidateForModals.user_manager_user_id,
                            ]}
                            onCancel={() =>
                                this.closeModal("sendMessageModalIsOpen")
                            }
                            closeModal={() =>
                                this.closeModal("sendMessageModalIsOpen")
                            }
                        />
                    </div>
                )}
            </PageInternal>
        );
    }
}
